import type { EventTypes, SportTypes } from '@racemap/sdk/schema/events';
import { type DBSortOrder, RacemapAPIClient, type ShowValues } from '@racemap/utilities/api-client';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { useCallback, useMemo } from 'react';
import type { SWRInfiniteResponse } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

const apiClient = RacemapAPIClient.fromWindowLocation();

const fetcher = async ([
  _1,
  searchValue,
  filter,
  show,
  eventTypes,
  sportTypes,
  startingAfter,
  startingBefore,
  endingAfter,
  endingBefore,
  sort,
  bbox,
  lastKey,
  pageSize,
]: [
  string,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  DBSortOrder | undefined,
  string | undefined,
  string | undefined,
  number,
]) => {
  return apiClient.getEventsPaged(
    {
      show: show ? (show.split(',') as Array<ShowValues>) : ['hidden'],
      filter,
      search: searchValue,
      findByEventTypes: eventTypes ? (eventTypes.split(',') as Array<EventTypes>) : undefined,
      findBySportTypes: sportTypes ? (sportTypes.split(',') as Array<SportTypes>) : undefined,
      startingBefore: startingBefore ? new Date(startingBefore) : undefined,
      startingAfter: startingAfter ? new Date(startingAfter) : undefined,
      endingBefore: endingBefore ? new Date(endingBefore) : undefined,
      endingAfter: endingAfter ? new Date(endingAfter) : undefined,
      sort,
      bbox,
    },
    pageSize,
    lastKey,
  );
};

interface UseEventsPaginatedParams {
  searchValue?: string;
  filter?: string;
  show?: Array<ShowValues>;
  eventTypes?: Array<EventTypes>;
  sportTypes?: Array<SportTypes>;
  startingAfter?: Date;
  startingBefore?: Date;
  endingAfter?: Date;
  endingBefore?: Date;
  sort?: DBSortOrder;
  bbox?: string;
  fetchDisabled?: boolean;
  keepPreviousData?: boolean;
  pageSize?: number;
}

export type UseEventsPaginatedMutate = SWRInfiniteResponse<{
  data: Array<RacemapEvent>;
  lastKey: string;
}>['mutate'];

export const useEventsPaginated = ({
  searchValue,
  filter,
  show,
  eventTypes,
  sportTypes,
  startingAfter,
  startingBefore,
  endingAfter,
  endingBefore,
  sort,
  bbox,
  fetchDisabled = false,
  keepPreviousData = true,
  pageSize = 30,
}: UseEventsPaginatedParams = {}): {
  data: Array<RacemapEvent>;
  isLoading: boolean;
  isValidating: boolean;
  error: unknown;
  loadNextPage: () => void;
  retry: () => void;
  hasNextPage: boolean;
  mutate: UseEventsPaginatedMutate;
  firstPageLoaded: boolean;
} => {
  const getKey = (
    pageIndex: number,
    previousPageData: { data: Array<RacemapEvent>; lastKey: string } | null,
  ):
    | [
        string,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        string | undefined,
        DBSortOrder | undefined,
        string | undefined,
        string | undefined,
        number,
      ]
    | null => {
    // Without these guards SWR can call getKey for page N+1 while page N is
    // still in flight. `previousPageData` is null then, so the cursor slot
    // collapses to undefined — the same key page 0 produces — and SWR reuses
    // page 0's cached data, duplicating items in the merged list.
    if (fetchDisabled) return null;
    if (pageIndex > 0 && previousPageData == null) return null;
    if (previousPageData != null && previousPageData.lastKey == null) return null;

    return [
      'EVENTS_PAGED',
      searchValue,
      filter,
      show?.join(','),
      eventTypes?.join(','),
      sportTypes?.join(','),
      startingAfter?.toISOString(),
      startingBefore?.toISOString(),
      endingAfter?.toISOString(),
      endingBefore?.toISOString(),
      sort,
      bbox,
      previousPageData?.lastKey,
      pageSize,
    ];
  };

  const { data, isLoading, isValidating, setSize, mutate, error } = useSWRInfinite(
    getKey,
    fetcher,
    {
      revalidateAll: false,
      keepPreviousData,
    },
  );

  const loadNextPage = useCallback(() => {
    setSize((s) => s + 1);
  }, [setSize]);

  const retry = useCallback(() => {
    void mutate();
  }, [mutate]);

  const events = useMemo(() => data?.flatMap((page) => page.data) || [], [data]);
  const firstPageLoaded = useMemo(() => data != null && data.length > 0, [data]);
  const hasError = useMemo(() => error != null, [error]);

  return {
    data: events,
    isLoading,
    isValidating,
    error,
    loadNextPage,
    retry,
    hasNextPage: !hasError && (data == null || data.at(-1)?.lastKey != null),
    mutate,
    firstPageLoaded,
  };
};
